import axios from 'axios';
import {
  ApiRouteVariant,
  ApiBusStop,
  ApiTablePeriod,
  ApiStopLinkShape,
  ApiRouteStops,
} from '../types/gtfs';

type AcquireTokenFunction = () => Promise<string>;

interface GtfsClientOptions {
  baseUrl: string;
  token?: string | AcquireTokenFunction;
}

export interface IGtfsClient {
  tablePeriods: () => Promise<ApiTablePeriod[]>;
  busStops: () => Promise<ApiBusStop[]>;
  routeStops: (tablePeriodID: number) => Promise<ApiRouteStops[]>;
  routes: (tablePeriodID: number) => Promise<ApiRouteVariant[]>;
  stopLinkShapes: (tablePeriodID: number) => Promise<ApiStopLinkShape[]>;
}

const getAuthorizationHeaderObject = async (
  token: string | AcquireTokenFunction | undefined,
): Promise<{ headers: { Authorization: string } }> => {
  const tokenValue = typeof token === 'function' ? await token() : token ?? '';
  return {
    headers: {
      Authorization: `Bearer ${tokenValue}`,
    },
  };
};

const GtfsClient = (opts: GtfsClientOptions): IGtfsClient => ({
  tablePeriods: fetchTablePeriods(opts),
  busStops: fetchBusStops(opts),
  routeStops: fetchRouteStops(opts),
  routes: fetchRoutes(opts),
  stopLinkShapes: fetchStopLinkShapes(opts),
});

const fetchTablePeriods =
  (opts: GtfsClientOptions) => async (): Promise<ApiTablePeriod[]> => {
    const { data, status } = await axios.get<ApiTablePeriod[]>(
      `${opts.baseUrl}/table_periods`,
      await getAuthorizationHeaderObject(opts.token),
    );
    if (status !== 200) {
      throw new Error(status.toString());
    }
    return data;
  };

const fetchBusStops =
  (opts: GtfsClientOptions) => async (): Promise<ApiBusStop[]> => {
    const { data, status } = await axios.get<ApiBusStop[]>(
      `${opts.baseUrl}/all_distinct_stops?location_type=0`,
      await getAuthorizationHeaderObject(opts.token),
    );
    if (status !== 200) {
      throw new Error(status.toString());
    }
    return data;
  };

const fetchRouteStops =
  (opts: GtfsClientOptions) =>
  async (tablePeriodID: number): Promise<ApiRouteStops[]> => {
    const { data, status } = await axios.get<ApiRouteStops[]>(
      `${opts.baseUrl}/route_stops?table_period_id=${tablePeriodID}`,
      await getAuthorizationHeaderObject(opts.token),
    );
    if (status !== 200) {
      throw new Error(status.toString());
    }
    return data;
  };

const fetchRoutes =
  (opts: GtfsClientOptions) =>
  async (tablePeriodID: number): Promise<ApiRouteVariant[]> => {
    const { data, status } = await axios.get<ApiRouteVariant[]>(
      `${opts.baseUrl}/routes?table_period_id=${tablePeriodID}`,
      await getAuthorizationHeaderObject(opts.token),
    );
    if (status !== 200) {
      throw new Error(status.toString());
    }
    return data;
  };

const fetchStopLinkShapes =
  (opts: GtfsClientOptions) =>
  async (tablePeriodID: number): Promise<ApiStopLinkShape[]> => {
    const { data, status } = await axios.get<ApiStopLinkShape[]>(
      `${opts.baseUrl}/stop_link_shapes?table_period_id=${tablePeriodID}`,
      await getAuthorizationHeaderObject(opts.token),
    );
    if (status !== 200) {
      throw new Error(status.toString());
    }
    return data;
  };

export default GtfsClient;
